import { ToggleButton as MuiToggleButton, useMediaQuery } from '@mui/material';
import { getHours } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BudgetPlannedData,
  ConversionRatePerformanceItem,
  useAsyncAction,
  useKpiTargetDistribution,
  useStoreInformation,
  useStoreOpeningHours,
  useStorePerformance,
  useStores,
  useTransaction,
  useUser,
} from '../../../../../hooks';
import { Container, Icon, Icons, Typography } from '../../../../../stories/atoms';
import { DropdownMenu } from '../../../../../stories/molecules';
import { ToggleButton } from '../../../../../stories/molecules/toggleButton/ToggleButton';
import { homeTranslations, kpiTargetTranslations } from '../../../../../translations';
import {
  formatAmountWithSeparator,
  getKpiTargetProps,
  getKpiTargetValue,
  isFailureResponse,
} from '../../../../../utils';

import {
  createStorePerformanceRequest,
  KpiDropdownActions,
  KpiType,
  StorePerformanceInterval,
} from '../../../../../constants';
import { useEventContext } from '../../../../../global-state/eventContext';
import { useCelebrateTarget } from '../../../../../hooks/useCelebrateTarget';
import { ContainerDrawer } from '../../../../../stories/molecules/container-drawer/ContainerDrawer';
import { muiTheme } from '../../../../../theme';
import {
  BudgetPlannedContent,
  getBudgetAndPlannedPermissions,
} from '../../../common/budgetPlannedContent/BudgetPlannedContent';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { isMultiDayInterval } from '../../../util';
import { ConversionRateGraphView } from './ConversionRateGraphView';
import { StyledRightContainerWrapper, StyledView, StyledWrapper } from './ConversionRateWidget.styles';
import { PayingNonPayingGraphView } from './PayingNonPayingGraphView';

export type ConversionRateWidgetProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
  hasPermissionToEditKpi: boolean;
  isNonSigninUser?: boolean;
  isToday: boolean;
  isKpiLoading: boolean;
  isKpiError: boolean;
  handleDropdownDetails: (kpiType: KpiType, action: KpiDropdownActions) => void;
  fromDate?: Date;
  toDate?: Date;
  onTargetAchieved: (target: string, date: Date) => void;
};

export const ConversionRateWidget: FC<ConversionRateWidgetProps> = observer(
  ({
    interval,
    currentDate,
    hasPermissionToEditKpi,
    isNonSigninUser,
    isToday,
    isKpiLoading,
    isKpiError,
    handleDropdownDetails,
    onTargetAchieved,
  }) => {
    const {
      get: { kpiTargetsProps, userPermissions },
    } = useUser();
    const { getKpiTargetDistribution } = useKpiTargetDistribution();
    const { fetchConversionRateWidgetData, fetchVisitorsBudget } = useTransaction();
    const { fetchForecastForKpis } = useStores();
    const { getStoreOpeningHours } = useStoreOpeningHours();
    const { timezone } = useStoreInformation();
    const { transactionEventsCount } = useEventContext();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

    const { canViewBudget, canViewPlanned, showBudgetAndPlanned } =
      getBudgetAndPlannedPermissions(userPermissions);

    const {
      performanceItems,
      performanceItemsLoading,
      performanceItemsError,

      filteredPerformanceItems,

      forecastPerformanceItems,
      triggerGetStorePerformance,
    } = useStorePerformance<ConversionRatePerformanceItem>();

    const {
      result: visitorsBudgetPlanned,
      isLoading: visitorsBudgetPlannedLoading,
      isError: visitorsBudgetPlannedError,
      triggerAction: triggerFetchVisitorsBudgetPlanned,
    } = useAsyncAction<BudgetPlannedData>();

    const target = getKpiTargetValue(kpiTargetsProps, 'dailyConversionRate');
    const [isBudgetSliderOpen, setIsBudgetSliderOpen] = useState(false);
    const [view, setView] = useState('view1');
    const [today, setToday] = useState(0);
    const [lastWeek, setLastWeek] = useState(0);
    const [lastYear, setLastYear] = useState(0);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

    const isTargetLocked = getKpiTargetProps(kpiTargetsProps, 'dailyConversionRate')?.isLocked ?? false;

    const { t } = useTranslation();

    const {
      conversionRate: conversionRateTranslation,
      kpiTargets,
      budgetPlanned: budgetPlannedTranslation,
    } = homeTranslations;

    const fetchData = useCallback(async () => {
      // Fetch the request based on interval
      const request = createStorePerformanceRequest({
        interval,
        currentDate,
      });

      const response = await fetchConversionRateWidgetData(request);

      if (isFailureResponse(response)) {
        throw new Error();
      }

      setToday(response.data.today);
      setLastWeek(response.data.lastWeek);
      setLastYear(response.data.lastYear);
      setLastUpdated(response.data.lastUpdated);

      return response.data.conversionRate;
    }, [interval, currentDate]);

    const fetchBudget = async () => {
      const request = createStorePerformanceRequest({
        interval,
        currentDate,
      });

      const response = await fetchVisitorsBudget(request);

      if (isFailureResponse(response)) {
        throw new Error();
      }

      return response.data;
    };

    const fetchForecastData = useCallback(async () => {
      const response = await fetchForecastForKpis<ConversionRatePerformanceItem>('conversionRate');

      if (isFailureResponse(response)) {
        throw new Error();
      }

      return response.data;
    }, [isToday]);

    const fetchWidgetData = () => {
      triggerGetStorePerformance({
        interval: interval,
        currentDate: currentDate,
        isToday: isToday,
        getDataAction: fetchData,
        getForecastDataAction: fetchForecastData,
        getHasValue: (item, currentInterval, openingHours, closingHours) => {
          // Disable checking values before and after closing hours for daily convesion rate
          if (
            currentInterval === StorePerformanceInterval.DAILY &&
            openingHours !== undefined &&
            closingHours !== undefined
          ) {
            const itemHour = Number.parseInt(item.timeUnit);

            // If the item occurs outside opening/closing hours we want to remove it but ONLY if no transaction
            // occured at that hour (i.e. item.payingCustomers === 0)
            if (item.payingCustomers === 0 && (itemHour < openingHours || itemHour > closingHours)) return false;
          }
          return item.payingCustomers !== 0 || item.nonPayingCustomers !== 0;
        },
      });
    };

    useEffect(() => {
      fetchWidgetData();

      triggerFetchVisitorsBudgetPlanned(fetchBudget);
    }, [currentDate, isNonSigninUser, transactionEventsCount]);

    const targetItems = getKpiTargetDistribution({
      performanceItems: performanceItems,
      kpiName: 'dailyConversionRate',
    });

    const isEditKPIDisabled = !hasPermissionToEditKpi;

    const handleChangeView = (event: React.MouseEvent<HTMLElement>, view2: string) => {
      if (view2 !== null) {
        setView(view2);
      }
    };

    const payingCustomersCounts = performanceItems.map(({ payingCustomers }) => {
      return Math.floor(payingCustomers);
    });

    const nonPayingCustomersCounts = performanceItems.map(({ nonPayingCustomers }) => {
      return Math.floor(nonPayingCustomers);
    });

    const totalVisitors = [...payingCustomersCounts, ...nonPayingCustomersCounts].reduce(
      (acc, curr) => acc + curr,
      0,
    );

    const allVisitorsZero = performanceItems.every(
      (item) => item.nonPayingCustomers === 0 && item.payingCustomers === 0,
    );
    const isNoData = !filteredPerformanceItems.some((item) => item !== null) || allVisitorsZero;

    const isPreviousDay = isMultiDayInterval(interval) || !isToday;

    // Only allow the celebration once the store has closed (end of day) so the goal is genuinely achieved.
    // Compare against the store's local time (not the device's) so timezone differences don't trigger it early.
    const { storeClosingHour } = getStoreOpeningHours(currentDate);
    const storeLocalHour = getHours(utcToZonedTime(new Date(), timezone));
    // If the store closes after 11pm, we consider it closed at 11pm for the purpose of this celebration.
    const effectiveClosingHour = Math.min(storeClosingHour, 23);
    const isStoreClosed = isToday && storeLocalHour >= effectiveClosingHour;

    useCelebrateTarget({
      target: target,
      currentValue: today,
      isToday: isToday,
      lastUpdated: lastUpdated ?? currentDate,
      localStorageKey: 'conversionRateTargetAchieved',
      enabled: isStoreClosed,
      onTargetAchieved: (targetSet: number, updatedDate: Date) => {
        const formattedTarget = formatAmountWithSeparator(targetSet, '%');
        onTargetAchieved(formattedTarget, updatedDate);
      },
    });

    const menuItems = [
      {
        key: target ? 'editKpi' : 'addKpi',
        disabled: isEditKPIDisabled || isTargetLocked || isPreviousDay,
        hasTooltip: isEditKPIDisabled || isTargetLocked || isPreviousDay,
        tooltipText: (
          <Container padding={[1]}>
            <Typography color="white">
              {!hasPermissionToEditKpi
                ? t(kpiTargets.noPermissionTooltip.key, kpiTargets.noPermissionTooltip.defaultValue)
                : isTargetLocked
                ? t(kpiTargets.lockedTargetTooltip.key, kpiTargets.lockedTargetTooltip.defaultValue)
                : isPreviousDay
                ? t(kpiTargets.previousDayTooltip.key, kpiTargets.previousDayTooltip.defaultValue)
                : t(kpiTargets.disabledTooltip.key, kpiTargets.disabledTooltip.defaultValue)}
            </Typography>
          </Container>
        ),
        item: (
          <Typography margin={[0, 2, 0]}>
            {target
              ? t(kpiTargets.editKpi.key, kpiTargets.editKpi.defaultValue)
              : t(kpiTargets.addKpi.key, kpiTargets.addKpi.defaultValue)}
          </Typography>
        ),
      },
      {
        key: 'showBudget',
        disabled: !showBudgetAndPlanned,
        hasTooltip: !showBudgetAndPlanned,
        tooltipText: (
          <Container padding={[1]}>
            <Typography color="white">
              {t(
                budgetPlannedTranslation.noPermissionBudgetTooltip.key,
                budgetPlannedTranslation.noPermissionBudgetTooltip.defaultValue,
              )}
            </Typography>
          </Container>
        ),
        item: (
          <Typography margin={[0, 2, 0]}>
            {t(budgetPlannedTranslation.budgetDropdown.key, budgetPlannedTranslation.budgetDropdown.defaultValue)}
          </Typography>
        ),
      },
    ];

    return (
      <ContainerDrawer
        isOpen={isBudgetSliderOpen}
        onClose={() => setIsBudgetSliderOpen(false)}
        sliderContent={
          <BudgetPlannedContent
            title={t(conversionRateTranslation.visitors.key, conversionRateTranslation.visitors.defaultValue)}
            currentData={totalVisitors}
            isLoading={visitorsBudgetPlannedLoading}
            isError={visitorsBudgetPlannedError}
            canViewBudget={canViewBudget}
            canViewPlanned={canViewPlanned}
            budgetPlanned={visitorsBudgetPlanned}
            budgetNoData={visitorsBudgetPlanned?.budget === 0}
            plannedNoData={visitorsBudgetPlanned?.planned === 0}
          />
        }
        width={isMobile ? '250px' : '300px'}
      >
        <WidgetCardContent
          headerIcon={Icons.SALESEFFICIENCY}
          headerText={t(conversionRateTranslation.title.key, conversionRateTranslation.title.defaultValue)}
          subHeaderText={t(
            conversionRateTranslation.subTitle.key,
            conversionRateTranslation.subTitle.defaultValue,
          )}
          lastUpdatedDate={lastUpdated}
          isLoading={performanceItemsLoading}
          isError={performanceItemsError}
          errorText={t(conversionRateTranslation.error.key, conversionRateTranslation.error.defaultValue)}
          errorRetryHandler={fetchWidgetData}
          target={
            target
              ? formatAmountWithSeparator(target, '%')
              : t(kpiTargetTranslations.notSet.key, kpiTargetTranslations.notSet.defaultValue)
          }
          isNoData={isNoData}
          noDataText={
            allVisitorsZero
              ? t(conversionRateTranslation.noVisitors.key, conversionRateTranslation.noVisitors.defaultValue)
              : undefined
          }
          headerSideContent={
            <StyledRightContainerWrapper>
              {performanceItems.length > 0 && (
                <StyledWrapper isMobile={isMobile}>
                  <ToggleButton
                    value={view}
                    onChange={handleChangeView}
                    leftButton={
                      <MuiToggleButton value="view1" aria-label="left button">
                        <Icon data-testid="left-icon" icon={Icons.CONVERSION} size="small" />
                      </MuiToggleButton>
                    }
                    rightButton={'123'}
                  />
                </StyledWrapper>
              )}

              <DropdownMenu
                data-testid="kpiDropdown"
                dropdownLabel={''}
                buttonContentProps={{
                  iconOptions: { customIcon: <Icon icon={Icons.MORE} /> },
                }}
                menuItems={menuItems}
                minWidth={'0px'}
                isCompact
                initialSelected={[]}
                onSelect={(selectedItem) => {
                  if ((selectedItem[0] === 'editKpi' || selectedItem[0] === 'addKpi') && !isEditKPIDisabled) {
                    handleDropdownDetails('dailyConversionRate', selectedItem[0] as KpiDropdownActions);
                  } else if (selectedItem[0] === 'showBudget') {
                    setIsBudgetSliderOpen(true);
                  }
                }}
                isOnlyIcon
                allowTooltip
              />
            </StyledRightContainerWrapper>
          }
          content={
            <Container wrap="nowrap" data-testid="conversion-rate-wrapper" height="100%">
              <StyledView isMobile={isMobile}>
                {view === 'view1' && (
                  <ConversionRateGraphView
                    target={target}
                    today={today}
                    interval={interval}
                    lastWeek={lastWeek}
                    lastYear={lastYear}
                    performanceItems={performanceItems}
                    filteredPerformanceItems={filteredPerformanceItems}
                    forecastPerformanceItems={forecastPerformanceItems}
                    targetItems={targetItems}
                    isKpiLoading={isKpiLoading}
                    isKpiError={isKpiError}
                    isToday={isToday}
                  />
                )}

                {view === 'view2' && (
                  <PayingNonPayingGraphView
                    interval={interval}
                    performanceItems={performanceItems}
                    filteredPerformanceItems={filteredPerformanceItems}
                  />
                )}
              </StyledView>
            </Container>
          }
          toolTipText={t(conversionRateTranslation.toolTip.key, conversionRateTranslation.toolTip.defaultValue)}
        />
      </ContainerDrawer>
    );
  },
);
